import React, { createContext, useContext, useState, useCallback } from "react";

const AppContext = createContext();

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [notifications, setNotifications] = useState([]);

  const login = useCallback((email, password) => {
    // Mock login
    if (email && password) {
      setUser({
        name: email.split("@")[0],
        email,
        avatar: email.charAt(0).toUpperCase(),
        joinDate: "April 2026",
      });
      addNotification("Welcome back! You have been logged in successfully.", "success");
      return true;
    }
    addNotification("Invalid credentials. Please try again.", "danger");
    return false;
  }, []);

  const signup = useCallback((email, password, confirmPassword) => {
    if (!email || !password) {
      addNotification("Please fill in all fields.", "danger");
      return false;
    }
    if (password !== confirmPassword) {
      addNotification("Passwords do not match.", "danger");
      return false;
    }
    if (password.length < 6) {
      addNotification("Password must be at least 6 characters.", "danger");
      return false;
    }
    setUser({
      name: email.split("@")[0],
      email,
      avatar: email.charAt(0).toUpperCase(),
      joinDate: "April 2026",
    });
    addNotification("Account created successfully! Welcome to Home Discovery.", "success");
    return true;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setFavorites([]);
    addNotification("You have been logged out.", "info");
  }, []);

  const toggleFavorite = useCallback((propertyId) => {
    setFavorites((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  }, []);

  const isFavorite = useCallback(
    (propertyId) => favorites.includes(propertyId),
    [favorites]
  );

  const addNotification = useCallback((message, type = "info") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <AppContext.Provider
      value={{
        user,
        login,
        signup,
        logout,
        favorites,
        toggleFavorite,
        isFavorite,
        searchTerm,
        setSearchTerm,
        notifications,
        addNotification,
        dismissNotification,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
